import React, { useContext, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  GroupDemandCarrierListPreviewOutDTO,
  PageResponseGroupDemandCarrierListPreviewOutDTO,
  StateEnum,
} from '@api/ringil-api.v2';
import { Clickable, Note, RingilHeading, RowWithSpaceBetween } from '@components/styles';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  dayjsFromDateAtMidnightInTz,
  dayjsFromApiUtcInTz,
  shortDateFormat,
  stringDateTimeWithLocaleFromApiStringInTz,
  getBrowserTimezone,
  apiUtcFromDayjs,
} from '@utils/dateWithTz';
import { InputText } from 'primereact/inputtext';
import { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import styled from 'styled-components';
import { convertToArray } from '@utils/utils';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import useNavigation from '@hooks/useNavigation';
import { FilterPage, useFilter } from '@hooks/useFilter/Filter.tsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';

interface ComponentProps {
}

interface FilterType {
  applicationId?: string;
  customerName?: string;
  validTill?: string;
  groupDemandState?: string[];
  userTimezoneId: string;
}

const MultiDemands: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const { caListGroupDemands } = useContext(ApiContext);
  const [data, setData] = useState<PageResponseGroupDemandCarrierListPreviewOutDTO>(undefined);
  const { loggedUser} = useContext(AuthenticatedUserContext)

  const navigation = useNavigation();

  const { Filter, applyFilterValue, filter, applyPaging, page, onPage } = useFilter<FilterType>({
    onLoad: (page: FilterPage, filter: FilterType) => {
      loadData(page.pageSize, page.pageNumber, filter);
    },
    filterName: 'multi_demands',
    defaultFilter: { userTimezoneId: getBrowserTimezone() } as FilterType,
  });

  // @ts-ignore
  const loadData = (pageSize, pageNumber, filter) => {
    const normalizedFilter = {
      ...filter,
      userTimezoneId: getBrowserTimezone(),
    };
    caListGroupDemands({ pageSize, pageNumber }, normalizedFilter, d => {
      setData(d);
    });
  };

  const { i18n } = useTranslation();

  const names = proxiedPropertiesOf<FilterType>();

  const renderButton = (row: GroupDemandCarrierListPreviewOutDTO) => {
    return (
      <>
        {row.state === StateEnum.NEW && row.isValid && (
          <Button
            label={tr(`MultiDemands.makeOffer`, `Make offer`)}
            onClick={e => {
              /*router.push(`/carrier/multi-demands/${row.groupDemandId}`);*/
              navigation.navigate(navigation.urlFunctions.createCarrierMultiDemandDetail(row.groupDemandId));
            }}
          />
        )}
      </>
    );
  };

  return (
    <>
      <div className={'flex flex-col gap-2'}>
        <BreadCrumbComponent items={[{ label: tr(`MultiDemands.title`, `Multi demands`) }]} />
        <COHeadline title={tr(`MultiDemands.title`, `Multi demands`)} variant={'h1'} />
      </div>

      <Filter />
      <FilterWrapper>
        <InputText
          value={filter?.applicationId}
          placeholder={tr('MultiDemands.filterByApplicationId', 'Filter by application ID')}
          onChange={e => applyFilterValue(names.applicationId, e.target.value)}
          data-testid="carrier-gd-filter-applicationId"
        />
        <InputText
          value={filter?.customerName}
          placeholder={tr(`MultiDemands.filterByCustomerName`, `Filter by customer name`)}
          onChange={e => applyFilterValue(names.customerName, e.target.value)}
        />
        <Calendar
          locale={i18n.language}
          readOnlyInput
          showButtonBar={true}
          showIcon
          onClearButtonClick={() => applyFilterValue('validTill', null)}
          value={filter?.validTill ? dayjsFromApiUtcInTz(filter.validTill, filter.userTimezoneId || getBrowserTimezone()).toDate() : null}
          dateFormat={shortDateFormat}
          onChange={e => applyFilterValue(names.validTill, e.value ? apiUtcFromDayjs(dayjsFromDateAtMidnightInTz(e.value as Date, filter.userTimezoneId || getBrowserTimezone()) as any) : null)}
        />
        <MultiSelect
          options={Object.keys(GroupDemandStateEnum).map(t => {
            return { code: t, name: tr(`GroupDemandStateEnum.${t}`, t) };
          })}
          value={convertToArray(filter?.groupDemandState)}
          optionLabel={'name'}
          optionValue={'code'}
          onChange={e => applyFilterValue(names.groupDemandState, e.value)}
          placeholder={tr('MultiDemands.filterByState', 'Filter by state')}
        ></MultiSelect>
      </FilterWrapper>
      {data && (
        <>
          {/*// @ts-ignore*/}
          <DataTable
            value={data.data}
            rows={data.size}
            paginator={true}
            lazy={true}
            totalRecords={data.total}
            onPage={onPage}
            first={(data.page - 1) * data.size}
            emptyMessage={tr(`global.noRecordFound`, `Nebyly nalezeny žádné záznamy`)}
          >
            <Column
              header={tr(`MultiDemands.id`, `ID`)}
              body={(row: GroupDemandCarrierListPreviewOutDTO) => (
                <div>
                  <Clickable>
                    {/*<Link href={`/carrier/multi-demands/${row.groupDemandId}`}>{row.groupDemandApplicationId}</Link>*/}
                    <Link
                      data-testid={`gd-row-${row.groupDemandApplicationId}-applicationId`}
                      to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierMultiDemandDetail(row.groupDemandId))}>
                      <div className={'flex flex-col'}>
                        {row.groupDemandApplicationId}
                        <Note data-testid={`gd-row-${row.groupDemandApplicationId}-name`}>{row.groupDemandName}</Note>
                        <Note data-testid={`gd-row-${row.groupDemandApplicationId}-id`}>{row.groupDemandId}</Note>
                      </div>
                    </Link>
                  </Clickable>
                </div>
              )}
            ></Column>
            <Column header={tr(`MultiDemands.company`, `Company`)}
                    body={(row: GroupDemandCarrierListPreviewOutDTO) => <div data-testid={`gd-row-${row.groupDemandApplicationId}-customerName`}>{row.createdByCompany}</div>}></Column>
            <Column
              header={tr(`MultiDemands.validTill`, `Valid till`)}
              body={(row: GroupDemandCarrierListPreviewOutDTO) => <div data-testid={`gd-row-${row.groupDemandApplicationId}-validTill`}>
                {stringDateTimeWithLocaleFromApiStringInTz(row.utcEnd ?? row.validTill, loggedUser.defaultTimezoneId ?? 'Europe/Prague')}
                <COTimezone dataTestId={`gd-row-${row.groupDemandApplicationId}-validTill-timezone`} timezoneId={loggedUser.defaultTimezoneId} />
              </div>}
            ></Column>
            <Column header={''} body={(row: GroupDemandCarrierListPreviewOutDTO) => renderButton(row)}></Column>
          </DataTable>
        </>
      )}
    </>
  );
};

export enum GroupDemandStateEnum {
  NEW = 'NEW',
  FINISHED = 'FINISHED',
}

const FilterWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

export default MultiDemands;
